import { PropsWithChildren, useLayoutEffect } from 'react';

import { AxiosResponse } from 'axios';
import { isEqual } from 'lodash';
import { useNavigate } from 'react-router-dom';

import { API_LOGIN_REFRESH } from '@/apis/auth/uri';
import axios from '@/apis/index';
import dialogInfo from '@/config/locale/snackBarInfo';
import { onDefaultPageCreated } from '@/context/redux/LoginUserInfoProvider';
import { onSnackBarOpen } from '@/context/redux/SnackBarProvider';
import { useAppDispatch, useAppSelector } from '@/context/redux/store';
import { routes } from '@/layouts/config/routes';
import { appendAuthToken, getAuthToken, getRefreshToken, removeAuthToken } from '@/utils/localStorage/auth';

let refreshPromise: Promise<AxiosResponse> | null = null;

const getAccessToken = () => {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = axios
    .post(API_LOGIN_REFRESH)
    .then((response) => {
      appendAuthToken(response.data.access_token, response.data.refresh_token);
      return response;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};

export const AxiosInterceptor = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { fingerprint } = useAppSelector((state) => state.loginUserInfo);

  useLayoutEffect(() => {
    const reqInterceptor = (config: any) => {
      const accessToken = getAuthToken();

      const { headers } = config;
      const header = {
        'Access-Control-Allow-Origins': '*',
        'Content-Type': 'application/json',
        fingerprint,
        ...headers,
      };

      if (accessToken) {
        header.Authorization = `Bearer ${accessToken}`;
      }
      if (config.url === API_LOGIN_REFRESH) {
        const refreshToken = getRefreshToken();
        header.Refresh = refreshToken;
      }

      // console.log({ ...config, headers: header });

      return { ...config, headers: header };
    };

    const reqErrInterceptor = async (error: any) => {
      return Promise.reject(error);
    };

    const resInterceptor = (response: any) => {
      // console.log(`============Response Interceptor Run :: Success :: ${response.config.url}`);
      // console.log(response);
      return response;
    };

    const resErrInterceptor = async (error: any) => {
      console.log('============Response Interceptor Run :: Error');
      const { status } = error.response.request;

      // 로그인 전 공개 엔드포인트(아이디/비밀번호 찾기: /login/mail/*)는 세션용 전역 401 처리
      // (토큰 갱신·"권한이 없습니다" 스낵바·로그인 리다이렉트) 대상에서 제외한다.
      // 각 페이지 핸들러가 자체 에러 메시지를 표시하도록 그대로 reject만 한다.
      const requestUrl: string = error.config?.url ?? '';
      if (requestUrl.includes('login/mail')) {
        return Promise.reject(error);
      }

      /*
            unAuthorized
              1. INFO_0007: 만료된 토큰인 경우
              2. 권한이 없는 경우
            */

      if (status === 401) {
        if (error.response.data.resCode === 'INFO_0007') {
          const { config: originalRequest } = error;
          const response = await getAccessToken();
          if (response.status === 200) {
            dispatch(onDefaultPageCreated(response.data.resUserDefaultInitPageVO));
            return axios(originalRequest);
          }
          if (response.status === 409) {
            // Conflict = 들고 온 refresh 토큰이 이미 다른 요청에서 사용되어 교체됨
            const originalToken = originalRequest.headers?.Authorization;
            const currentToken = getAuthToken();
            const isTokenUpdated = !isEqual(`Bearer ${currentToken}`, originalToken);
            console.debug(`[AxiosInterceptor] isTokenUpdated: ${isTokenUpdated}\n${originalToken}\n${currentToken}`);
            if (isTokenUpdated) {
              return axios(originalRequest);
            }
            dispatch(
              onSnackBarOpen({
                type: 'error',
                msg: dialogInfo.ERROR_UNAUTHORIZED_0001,
              }),
            );
            removeAuthToken();
            navigate(routes.MEMBER_LOGIN.build({}), { replace: true });
          }
          return Promise.reject(error);
        }
        if (error.response.data.resCode === 'INFO_0018') {
          dispatch(
            onSnackBarOpen({
              type: 'error',
              msg: dialogInfo.ERROR_014,
            }),
          );
        }
        if (error.response.data.resCode.includes('ERROR_LOGOUT_')) {
          dispatch(
            onSnackBarOpen({
              type: 'error',
              msg: dialogInfo.ERROR_UNAUTHORIZED_0001,
            }),
          );
          removeAuthToken();
          navigate(routes.MEMBER_LOGIN.build({}), { replace: true });
        }
        return Promise.reject(error);
      }
      if (status === 404) {
        navigate(routes.ERROR_404.build({}), { replace: true });
      }
      if (status === 409) {
        const { config: originalRequest } = error;
        if (originalRequest.url === API_LOGIN_REFRESH) {
          // Conflict = 들고 온 refresh 토큰이 이미 다른 요청에서 사용되어 교체됨 (에러 전달)
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    };

    const requestInterceptor = axios.interceptors.request.use(reqInterceptor, reqErrInterceptor);
    const responseInterceptor = axios.interceptors.response.use(resInterceptor, resErrInterceptor);

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [dispatch, fingerprint, navigate]);

  return <>{children}</>;
};
